import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * ADM-02 — INTEGRATIONS & API KEYS (spec §15.7).
 *
 * "Two halves: six things you can switch on or off, and a list of keys."
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE CONCLUSION THIS FILE IS BUILT ON, stated first because it decides
 * everything below: THE SIX INTEGRATIONS ARE DEPLOY-TIME VENDOR WIRING, AND
 * THE KEYS ARE A REAL PRODUCT FEATURE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Read §5's six against DECISION.md and every one of them is a decision the
 * OWNER has not yet made and a credential the PRODUCT does not hold:
 *
 *   Payment Gateway  → §8.7, "the single highest-risk unknown". `PAYMENT_
 *                      PROVIDER` defaults to `none` and `none` THROWS.
 *   E-Invoicing      → §8.10. One jurisdiction registry, `mock` implemented,
 *                      ZATCA and PS/JO named TODOs.
 *   Email / SMTP     → §2.12. A sender adapter chosen by environment.
 *   Cloud Storage    → §2.15. R2, keyed and presigned, with no bucket yet.
 *   Bank Feed        → no adapter and no vendor named anywhere.
 *   Webhooks         → no outbound seam at all.
 *
 * So a **Connect** button here would flip a boolean that no adapter consults —
 * which is EXACTLY the `multi_branch` defect the acceptance sweep found ("one
 * of three paid modules is a switch wired to nothing"), rebuilt six times. The
 * screen therefore REPORTS configuration rather than pretending to set it: a
 * card says whether the deployment has that vendor wired, and says how it is
 * wired when it is not. `connected` is computed from the environment on every
 * request and is not a column anywhere.
 *
 * THE KEYS ARE THE OPPOSITE. A key is a credential the product mints, stores,
 * scopes and destroys entirely on its own, and every rule §12 gives it is
 * enforceable today. That half is built properly.
 *
 * ── WHAT A KEY CAN REACH, AND WHY THAT IS AN ALLOWLIST OF ZERO ────────────
 *
 * BR-ADM-02-04: "A key must carry a scope, and THE SCOPE MUST REALLY LIMIT
 * WHAT IT CAN REACH. Read only must not be able to change anything."
 *
 * The mechanism is real: `ApiKeyGuard` authenticates a presented key, resolves
 * it to a tenant and a scope, and hands `PermissionGuard` the permission set
 * `SCOPE_PERMISSIONS` grants — so a `read` key holds only `*.view` and a write
 * route refuses it through the same guard that refuses a person. On top of
 * that sits a second, blunter gate: a route is reachable by a key ONLY if it
 * carries `@ApiAccessible`, and NO ROUTE CARRIES IT YET.
 *
 * That is deliberate and it is the security decision, not an oversight.
 * Opening the product to non-human callers settles things this wave has no
 * business settling alone: which routes an outside program may call, what
 * happens to rate limits when the caller is a script, whether a key may ever
 * reach payroll (16.2.3 keeps it apart from `hr` for a reason), and what an
 * abuse response looks like. An allowlist that starts empty makes each of
 * those a one-line, greppable decision — the same shape `@Public` and
 * `@SelfService` already have — instead of a surface somebody discovers.
 */
// ── The six integrations (§5) ───────────────────────────────────────────────
export const INTEGRATIONS = [
    'payment_gateway',
    'einvoicing',
    'bank_feed',
    'email',
    'webhooks',
    'cloud_storage',
];
export const IntegrationSchema = z.enum(INTEGRATIONS);
/**
 * §5's "Where it links to" column — three of the six feed a screen. Kept here
 * rather than in the component so the set is one list: §18's definition of
 * done counts them ("Three cards carry a deep link").
 */
export const INTEGRATION_LINK = {
    payment_gateway: '/pos',
    einvoicing: '/accounting/einvoicing',
    bank_feed: '/banks/accounts',
};
export const IntegrationStateSchema = z
    .object({
    integration: IntegrationSchema,
    /**
     * COMPUTED from the deployment, never stored. See the file header: a
     * stored flag here would be a switch wired to nothing.
     */
    connected: z.boolean(),
    /**
     * What is actually configured, when anything is — `paddle`, `zatca`,
     * `resend`, `mock`. Shown so an administrator can tell a real connection
     * from a development one at a glance, which a green pill alone cannot say.
     */
    provider: z.string().nullable(),
})
    .strict();
// ── Keys (§6) ───────────────────────────────────────────────────────────────
/** §6 field 7 — "Full access, Sales only, or Read only." */
export const KEY_SCOPES = ['full', 'sales', 'read'];
export const KeyScopeSchema = z.enum(KEY_SCOPES);
/**
 * BR-ADM-02-04 made concrete — WHAT EACH SCOPE HOLDS.
 *
 * Expressed as the ordinary 12×5 permissions of PART 16.2.2 rather than as a
 * parallel vocabulary, so `PermissionGuard` refuses a key exactly as it
 * refuses a person and there is one implementation of "may this caller do
 * this". A second permission system for machines is how the two drift.
 *
 *   read  — every area's `view` AND NOTHING ELSE. "Read only must not be able
 *           to change anything" is then a property of the list, checkable by
 *           reading it, rather than a promise made in a handler.
 *   sales — the sales chain a till or an order feed needs: sales and crm, plus
 *           the reads either one has to make (an item's price, a partner's
 *           balance). No approve anywhere: approving is a person's act
 *           (16.5), and a key is not a person.
 *   full  — every area except PAYROLL. R15/BR-PERM-39 keep salaries to the
 *           Owner flag alone and say no role may be given them by default; a
 *           key is further from a person than any role, so "full access" stops
 *           short of the one area the Owner has to grant deliberately. §6's
 *           label says Full access and this is what full access means here —
 *           stated, so nobody has to infer it from a missing row.
 */
export const SCOPE_PERMISSIONS = {
    read: [
        'sales.view',
        'purchasing.view',
        'inventory.view',
        'manufacturing.view',
        'crm.view',
        'accounting.view',
        'banking.view',
        'hr.view',
        'projects.view',
        'reports.view',
    ],
    sales: [
        'sales.view',
        'sales.create',
        'sales.edit',
        'crm.view',
        'crm.create',
        'crm.edit',
        'inventory.view',
        'reports.view',
    ],
    full: [
        'sales.view', 'sales.create', 'sales.edit', 'sales.delete', 'sales.approve',
        'purchasing.view', 'purchasing.create', 'purchasing.edit', 'purchasing.delete', 'purchasing.approve',
        'inventory.view', 'inventory.create', 'inventory.edit', 'inventory.delete', 'inventory.approve',
        'manufacturing.view', 'manufacturing.create', 'manufacturing.edit', 'manufacturing.delete', 'manufacturing.approve',
        'crm.view', 'crm.create', 'crm.edit', 'crm.delete', 'crm.approve',
        'accounting.view', 'accounting.create', 'accounting.edit', 'accounting.delete', 'accounting.approve',
        'banking.view', 'banking.create', 'banking.edit', 'banking.delete', 'banking.approve',
        'hr.view', 'hr.create', 'hr.edit', 'hr.delete', 'hr.approve',
        'projects.view', 'projects.create', 'projects.edit', 'projects.delete', 'projects.approve',
        'reports.view', 'reports.create', 'reports.edit', 'reports.delete', 'reports.approve',
        // `admin` is absent too: a key that could edit roles could widen itself.
    ],
};
/**
 * §6 field 6 — "the prefixes carry meaning: `pk_live_` for a key that can
 * change things, `pk_read_` for one that cannot."
 *
 * So the prefix is DERIVED from the scope and never chosen, which makes the
 * meaning true rather than conventional: a `pk_read_` key that could write
 * would be a lie told by a string.
 */
export const SCOPE_PREFIX = {
    full: 'pk_live_',
    sales: 'pk_live_',
    read: 'pk_read_',
};
/**
 * BR-ADM-02-09 — "Keys must expire, or must at least be FLAGGED when unused
 * for a long time. The period is [NOT IN PROTOTYPE — decide at build time];
 * PROPOSED: flag any key unused for 90 days."
 *
 * DECIDED: flagged at 90 days, not expired.
 *
 * The rule offers both and the difference matters. An automatic expiry breaks
 * a working integration on a schedule nobody is watching — ERR-ADM-02-01's
 * "the till stops" happening by itself, at 3am, for a key that was doing its
 * job. A flag puts the same fact in front of the administrator and lets a
 * person decide, which is what §9's own note asks for ("a build SHOULD add an
 * Expired state, so a key nobody has used for a long time stops working rather
 * than sitting live for ever") without taking the decision away from them.
 * Turning the flag into a real expiry is a one-line change to the guard once
 * the owner says so.
 */
export const KEY_STALE_DAYS = 90;
export const ApiKeySchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    /** §6 field 6 — display only. The secret is not retrievable (BR-ADM-02-01). */
    prefix: z.string(),
    scope: KeyScopeSchema,
    createdAt: z.iso.datetime(),
    createdBy: z.string().nullable(),
    /** BR-ADM-02-08 — null renders as a dash, never as a date. */
    lastUsedAt: z.iso.datetime().nullable(),
    /** BR-ADM-02-09's flag: never used, or unused for KEY_STALE_DAYS. */
    stale: z.boolean(),
})
    .strict();
export const CreateApiKeyRequestSchema = z
    .object({ name: z.string().trim().min(1).max(120), scope: KeyScopeSchema })
    .strict();
/**
 * BR-ADM-02-01 — "A key's full value must be shown EXACTLY ONCE, when it is
 * created, and must never be retrievable afterwards."
 *
 * `token` appears in this response and in no other shape in the product. There
 * is no route that returns it again, because there is nothing to return it
 * from: the database holds a SHA-256 digest.
 */
export const CreateApiKeyResponseSchema = z
    .object({
    key: ApiKeySchema,
    /** The only time these bytes exist outside the caller's own memory. */
    token: z.string(),
})
    .strict();
export const API_KEY_MESSAGES = {
    /** §8's confirmation body, verbatim — the shared box, not a new dialog. */
    revokeWarning: 'Any integration using this key will immediately lose access. This cannot be undone.',
    /** ERR-ADM-02-02 — closing the new-key box. */
    copyWarning: 'You will not be able to see this key again. Are you sure you have copied it?',
    /** §17 — "two administrators revoking the same key. The second attempt MUST SAY THE KEY IS ALREADY GONE, not fail silently." */
    alreadyRevoked: 'That key has already been revoked.',
    /** §17's Empty row — "a line saying so, not a headerless table". */
    noKeys: 'No API keys yet. A key lets a program read or write this company’s data.',
    /**
     * The sentence on every integration card whose vendor is not wired. It says
     * WHERE the connection is made, because §7 admits the prototype's Connect
     * button "only flips a switch" and the honest build does not offer one.
     */
    wiredAtDeployment: 'Connections are configured when your system is deployed, not from this screen. Ask your vendor to set one up.',
};
export const apiKeysContract = c.router({
    list: {
        method: 'GET',
        path: '/api/integrations',
        responses: {
            200: z
                .object({
                integrations: IntegrationStateSchema.array(),
                keys: ApiKeySchema.array(),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'ADM-02 §4: the six integrations’ real configuration state, and every LIVE key. Never a secret.',
    },
    create: {
        method: 'POST',
        path: '/api/integrations/keys',
        body: CreateApiKeyRequestSchema,
        responses: {
            201: CreateApiKeyResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'BR-ADM-02-01: mints a key and returns its value ONCE. Audited (BR-ADM-02-05).',
    },
    revoke: {
        method: 'POST',
        path: '/api/integrations/keys/:keyId/revoke',
        pathParams: z.object({ keyId: z.uuid() }).strict(),
        body: z.object({}).strict(),
        responses: { 200: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-ADM-02-02: immediate and not undoable. 409 when somebody else got there first (§17).',
    },
    /**
     * §17's last edge case, which the prototype has no answer to: "A KEY LEAKS.
     * There must be a way to revoke every key at once. The prototype has none,
     * and one SHOULD be added."
     */
    revokeAll: {
        method: 'POST',
        path: '/api/integrations/keys/revoke-all',
        body: z.object({}).strict(),
        responses: {
            200: z.object({ revoked: z.number().int().min(0) }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: '§17: revoke every live key at once, for the day one leaks.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=api-keys.js.map